import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Shell } from "@/components/Shell";
import { api } from "@/lib/api";
import { SEOHead, JsonLd } from "@/components/SEO";
import { VendorCard, ReviewSnippet } from "@/pages/seo/_components";
import { Stethoscope, MapPin, Star } from "lucide-react";

/**
 * /vendors/{category-slug} — e.g. /vendors/chiropractor
 *
 * Owns category-level intent ("find a chiropractor for our health fair").
 * Renders: count + benefit copy, featured (top-rated) vendors, all vendors,
 * city cross-links, recent reviews. JSON-LD: CollectionPage + ItemList.
 */
export default function VendorCategoryPage() {
    const { slugOrId } = useParams();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get(`/seo/categories/${slugOrId}`);
                if (!cancelled) setData(r.data);
            } catch (e) { if (!cancelled) setErr(e); }
        })();
        return () => { cancelled = true; };
    }, [slugOrId]);

    if (err) return <Shell><div className="p-10 text-brand-mute">Failed to load.</div></Shell>;
    if (!data) return <Shell><div className="p-10 text-brand-mute">Loading…</div></Shell>;

    const cat = data.category;
    const n = data.vendor_count;
    const titleCore = `${cat.name} vendors for corporate health fairs`;
    const description = `HealthFairX features ${n} ${cat.name.toLowerCase()} vendor${n === 1 ? "" : "s"} ready to participate in employee health fairs. Browse trusted ${cat.name.toLowerCase()} partners, read verified host reviews, and book your event.`;

    const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: typeof window !== "undefined" ? window.location.origin : "" },
            { "@type": "ListItem", position: 2, name: "Vendors", item: typeof window !== "undefined" ? `${window.location.origin}/vendors` : "" },
            { "@type": "ListItem", position: 3, name: cat.name },
        ],
    };
    const itemList = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: titleCore,
        numberOfItems: n,
        itemListElement: data.vendors.slice(0, 25).map((v, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: typeof window !== "undefined" ? `${window.location.origin}/vendors/${v.vendor_slug || v.id}` : undefined,
            name: v.business_name,
        })),
    };

    return (
        <Shell>
            <SEOHead title={titleCore} description={description} />
            <JsonLd data={breadcrumb} />
            <JsonLd data={itemList} />

            <section className="bg-brand-bone border-b border-brand-line">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
                    <div className="text-xs uppercase tracking-wider text-brand-mute font-bold flex items-center gap-2">
                        <Link to="/vendors" className="hover:text-brand-forest">Vendors</Link>
                        <span>/</span>
                        <span className="text-brand-ink">{cat.name}</span>
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3 flex items-center gap-3" data-testid="seo-category-h1">
                        <Stethoscope size={36} className="text-brand-forest" /> {cat.name} vendors
                    </h1>
                    <p className="mt-3 text-brand-mute max-w-3xl">
                        {n > 0
                            ? `${n} ${cat.name.toLowerCase()} vendor${n === 1 ? "" : "s"} on HealthFairX ready to bring ${cat.name.toLowerCase()} services to your next employee health fair.`
                            : `No ${cat.name.toLowerCase()} vendors are listed yet. Be the first — `}
                        {n === 0 && <Link to="/register" className="text-brand-forest font-semibold underline">join the marketplace</Link>}
                        {n === 0 && "."}
                    </p>
                </div>
            </section>

            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 space-y-12">
                {/* Featured / top-rated */}
                {data.featured.length > 0 && (
                    <section data-testid="seo-category-featured">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <Star size={20} className="text-amber-500 fill-amber-400" /> Top {cat.name.toLowerCase()} vendors
                        </h2>
                        <p className="text-sm text-brand-mute mt-1">Ranked by host reviews and event participation.</p>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                            {data.featured.map((v) => <VendorCard key={v.id} v={v} testid={`seo-featured-${v.id}`} />)}
                        </div>
                    </section>
                )}

                {/* Cities cross-links */}
                {data.cities.length > 0 && (
                    <section data-testid="seo-category-cities">
                        <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                            <MapPin size={20} className="text-brand-forest" /> Where {cat.name.toLowerCase()} vendors serve
                        </h2>
                        <div className="flex flex-wrap gap-2 mt-4">
                            {data.cities.map((c) => (
                                <Link key={c.slug} to={`/vendors/${c.slug}/${cat.slug}`}
                                      data-testid={`seo-city-link-${c.slug}`}
                                      className="pill px-4 py-2 text-sm font-semibold bg-white border border-brand-line hover:border-brand-forest hover:bg-brand-mint">
                                    {cat.name} in {c.city}{c.state ? `, ${c.state}` : ""}
                                    <span className="text-xs text-brand-mute ml-1.5">({c.vendor_count})</span>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* All vendors */}
                {data.vendors.length > 0 && (
                    <section data-testid="seo-category-all">
                        <h2 className="font-heading text-2xl font-bold">All {cat.name.toLowerCase()} vendors</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
                            {data.vendors.map((v) => <VendorCard key={v.id} v={v} />)}
                        </div>
                    </section>
                )}

                {/* Recent reviews */}
                {data.recent_reviews.length > 0 && (
                    <section data-testid="seo-category-reviews">
                        <h2 className="font-heading text-2xl font-bold">Recent reviews in {cat.name.toLowerCase()}</h2>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5">
                            {data.recent_reviews.map((r) => <ReviewSnippet key={r.id} r={r} />)}
                        </div>
                    </section>
                )}
            </div>
        </Shell>
    );
}
